/**
 * Regenerate-diagram hint message (#1117), shared by the browser (which builds
 * and sends it) and the API (which enforces the retry cap on it).
 *
 * The hint is an ordinary chat message whose first line is a marker:
 *
 *     [regen:<block-hash>:<failure-class>]
 *
 * The API counts earlier user messages in the same session carrying the same
 * marker and answers 429 beyond the cap. The cap lives in agent_messages
 * (durable across restarts, per session, no migration).
 *
 * What it is and is not: a guard against repeated or runaway Regenerate clicks
 * from the shipped client. It is NOT cost control and a hand-written client can
 * send the same text without the marker; spend is bounded by the token cap and
 * the platform rate bucket. Known residuals (review log, R2 B2/B3): a session
 * member who deletes an earlier hint message resets the count, and concurrent
 * identical requests can overshoot the cap by the number of requests in flight.
 */

export type DiagramFailureClass = 'parse' | 'lexical' | 'render' | 'other';

/** Max regenerate requests per (code block, failure class) per session. */
export const REGENERATE_MAX_PER_BLOCK = 1;
/** Backstop: max regenerate requests of any kind per session. */
export const REGENERATE_MAX_PER_SESSION = 5;

const MARKER_RE = /^\[regen:([0-9a-f]{12}):(parse|lexical|render|other)\][ \t]*(?:\r?\n|$)/;

/** Whitespace-insensitive 48-bit FNV-1a hash of the diagram source (non-crypto). */
export function hashDiagramCode(code: string): string {
  const norm = code.replace(/\s+/g, ' ').trim();
  // 2 x 32-bit FNV-1a lanes with different offsets -> 12 hex chars.
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193 ^ 0x9e3779b9;
  for (let i = 0; i < norm.length; i++) {
    const c = norm.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x85ebca6b) >>> 0;
  }
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).slice(0, 12);
}

export function classifyDiagramError(message: string): DiagramFailureClass {
  if (/lexical error/i.test(message)) return 'lexical';
  if (/parse error|expecting|syntax/i.test(message)) return 'parse';
  if (/failed to initialise|render/i.test(message)) return 'render';
  return 'other';
}

export function regenerateMarker(code: string, failure: DiagramFailureClass): string {
  return `[regen:${hashDiagramCode(code)}:${failure}]`;
}

/** Parse the marker off the start of a message; null when it carries none. */
export function parseRegenerateMarker(content: string): { hash: string; failure: DiagramFailureClass; marker: string } | null {
  const m = MARKER_RE.exec(content);
  if (!m) return null;
  return { hash: m[1], failure: m[2] as DiagramFailureClass, marker: `[regen:${m[1]}:${m[2]}]` };
}

/** Strip the marker line for display in the chat bubble. */
export function stripRegenerateMarker(content: string): string {
  return content.replace(MARKER_RE, '');
}

/** The chat message sent when the user clicks "Regenerate diagram". */
export function buildRegenerateMessage(code: string, errorMessage: string): string {
  const failure = classifyDiagramError(errorMessage);
  const firstLine = errorMessage.split('\n').find(l => l.trim())?.trim().slice(0, 300) ?? 'Invalid diagram syntax';
  const fence = '```';
  return [
    regenerateMarker(code, failure),
    'The last diagram did not render. Please send a corrected version of it only.',
    '',
    `Mermaid parser said: ${firstLine}`,
    '',
    'Rules: keep the same content and diagram type; quote any label that contains punctuation; do not use parentheses or semicolons inside labels or messages; close every block.',
    '',
    'The diagram that failed:',
    `${fence}mermaid`,
    code.trim().slice(0, 6000),
    fence,
  ].join('\n');
}
